import { BreakpointObserver } from '@angular/cdk/layout';
import { DOCUMENT } from '@angular/common';
import { Injectable, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';

import { Observable, fromEvent, map } from 'rxjs';

import { ResizeObserverService } from '../resize-observer.service';
import { WINDOW } from '../window/window.token';
import { DeviceConfig } from './device.client-config';

/**
 * @whatItDoes Provides information about current device
 *
 * @description
 *
 * Use to check device type and orientation.
 *
 * @stable
 */
@Injectable({
    providedIn: 'root',
})
export class DeviceService {
    readonly #document = inject(DOCUMENT);
    readonly #window = inject(WINDOW);
    readonly #breakpointObserver = inject(BreakpointObserver);
    readonly #resizeObserver = inject(ResizeObserverService);
    private readonly deviceConfig = inject(DeviceConfig);

    readonly isMobileWidth = computed(() => this.windowRect().width < 768);
    readonly windowWidth = computed(() => this.windowRect().width);
    readonly #orientation$ = this.#breakpointObserver.observe('(orientation: landscape)').pipe(map((s) => (s.matches ? 'landscape' : 'portrait')));
    readonly orientationSignal = toSignal(this.#orientation$, { requireSync: true });
    private readonly propsCache = new Map<string, any>();
    readonly windowRect = toSignal(
        this.#resizeObserver
            .observe(this.#document.documentElement)
            .pipe(map(({ contentBoxSize }) => ({ width: contentBoxSize[0]?.inlineSize || 0, height: contentBoxSize[0]?.blockSize || 0 }))),
        {
            initialValue: {
                width: this.#window.innerWidth,
                height: this.#window.innerHeight,
            },
        },
    );
    readonly visualViewport = toSignal(
        fromEvent(this.#window.visualViewport!, 'resize').pipe(
            map(() => ({
                width: this.#window.visualViewport!.width,
                height: this.#window.visualViewport!.height,
            })),
        ),
        {
            initialValue: {
                width: this.#window.visualViewport!.width,
                height: this.#window.visualViewport!.height,
            },
        },
    );
    readonly visualViewportWidth = computed(() => this.visualViewport().width);
    readonly visualViewportHeight = computed(() => this.visualViewport().height);

    get deviceType(): string {
        if (!this.isMobile) {
            return 'desktop';
        }
        if (this.isTablet) {
            return 'tablet';
        }
        return 'phone';
    }

    get orientation(): Observable<'landscape' | 'portrait'> {
        return this.#orientation$;
    }

    get currentOrientation(): string {
        return this.orientationSignal();
    }

    get isAndroid(): boolean {
        return this.cache('isAndroid', () => this.deviceConfig.isAndroid);
    }

    get isiOS(): boolean {
        return this.cache('isiOS', () => this.deviceConfig.isIOS);
    }

    get isChrome(): boolean {
        return this.cache('isChrome', () => /Chrome/g.test(this.#window.navigator.userAgent));
    }

    get isNexus(): boolean {
        return this.cache('isNexus', () => /Nexus/g.test(this.#window.navigator.userAgent));
    }

    get isMobile(): boolean {
        return this.cache('isMobile', () => this.deviceConfig.isMobile);
    }

    get isMobilePhone(): boolean {
        return this.cache('isMobilePhone', () => this.deviceConfig.isMobilePhone);
    }

    get isTouch(): boolean {
        return this.cache('isTouch', () => this.deviceConfig.isTouch);
    }

    get isTablet(): boolean {
        return this.cache('isTablet', () => this.deviceConfig.isTablet);
    }

    get isRobot(): boolean {
        return this.cache('isRobot', () => this.deviceConfig.isRobot);
    }

    get cpuMaxFrequency(): string | undefined {
        return this.cache('cpuMaxFrequency', () => this.deviceConfig.cpuMaxFrequency);
    }

    get totalRam(): string | undefined {
        return this.cache('totalRam', () => this.deviceConfig.totalRam);
    }

    get cpuCores(): string | undefined {
        return this.cache('cpuCores', () => this.deviceConfig.cpuCores);
    }

    get model(): string | undefined {
        return this.cache('model', () => this.deviceConfig.model);
    }

    get osName(): string {
        return this.cache('oSName', () => this.deviceConfig.osName);
    }

    get osVersion(): string {
        return this.cache('oSVersion', () => this.deviceConfig.osVersion);
    }

    get vendor(): string {
        return this.cache('vendor', () => this.deviceConfig.vendor);
    }

    get displayWidth(): string | undefined {
        return this.cache('displayWidth', () => this.deviceConfig.displayWidth);
    }

    get displayHeight(): string | undefined {
        return this.cache('displayHeight', () => this.deviceConfig.displayHeight);
    }

    getCapability(key: string): string | undefined {
        return this.deviceConfig?.properties[key];
    }

    private cache<T>(key: string, fn: () => T): T {
        if (this.propsCache.has(key)) {
            return this.propsCache.get(key);
        }

        const value = fn();
        this.propsCache.set(key, value);
        return value;
    }
}
